/* ============================================================
   Super Subscriptions
   First Box Free Gifts + Loyalty Rewards Integration
   Aurora-compatible / brand-agnostic
   ============================================================ */

(() => {
    "use strict";

    /* ============================================================
       SELECTORS / CLASSES
       ============================================================ */

    const ROOT_SELECTOR = ".super-simple-subs-wrapper";
    const PLAN_SELECTOR = ".sss-aurora-option.subscription-option";
    const GIFT_BLOCK_SELECTOR = ".sss-plan-gift";
    const GIFT_ROW_SELECTOR = "[data-plan-gift-row]";
    const LOYALTY_SELECTOR = ".sss-loyalty-bonus";
    const DESCRIPTION_SELECTOR = ".sss-aurora-inline-description";

    const CUSTOM_CLASS = "super-subscriptions-free-gifts";
    const NATIVE_HIDDEN_CLASS = "super-subscriptions-free-gifts-native-hidden";
    const LOYALTY_HIDDEN_CLASS = "super-subscriptions-loyalty-integrated-hidden";

    const SAVE_BADGE_CLASS = "ssfg-save-badge";
    const NATIVE_DISCOUNT_HIDDEN_CLASS = "ssfg-native-discount-hidden";

    const DESCRIPTION_PILL_CLASS = "ssfg-description-pill";
    const DESCRIPTION_SOURCE_HIDDEN_CLASS = "ssfg-description-source-hidden";
    const DESCRIPTION_CARD_CLASS = "ssfg-has-description-pill";
    const DESCRIPTION_OVERFLOW_CLASS = "ssfg-description-overflow-host";


    /* ============================================================
       CACHES / STATE
       ============================================================ */

    const productCache = new Map();
    const searchCache = new Map();

    const catalogCache = {
        promise: null
    };

    const loyaltyPending = new WeakMap();
    const loyaltyValueCache = new WeakMap();

    let refreshFrame = null;
    let enhancing = false;


    /* ============================================================
       BASIC HELPERS
       ============================================================ */

    function clean(value) {
        return String(value ?? "")
            .replace(/\u00a0/g, " ")
            .replace(/\s+/g, " ")
            .trim();
    }


    function normalise(value) {
        return clean(value)
            .toLowerCase()
            .replace(/\s*\((?:one[- ]?time|subscription)\)\s*$/i, "")
            .replace(/\s+-\s+default title\s*$/i, "")
            .replace(/[^a-z0-9]+/g, " ")
            .trim();
    }


    function textOf(element) {
        return clean(
            element?.innerText ||
            element?.textContent ||
            ""
        );
    }


    function rawTextOf(element) {
        return clean(
            element?.textContent ||
            ""
        );
    }


    function escapeRegExp(value) {
        return String(value)
            .replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    }


    function safeUrl(url) {
        const value = clean(url);

        if (!value) {
            return "";
        }

        if (value.startsWith("//")) {
            return window.location.protocol + value;
        }

        return value;
    }


    function getHandleFromUrl(url) {
        const match = String(url || "")
            .match(/\/products\/([^/?#]+)/i);

        return match
            ? decodeURIComponent(match[1])
            : "";
    }


    function makeElement(
        tag,
        className,
        text
    ) {
        const element = document.createElement(tag);

        if (className) {
            element.className = className;
        }

        if (text !== undefined) {
            element.textContent = text;
        }

        return element;
    }


    function displayNameFromTitle(title) {
        return clean(title)
            .replace(/^[🎁\s]+/u, "")
            .replace(/\s*\((?:one[- ]?time|subscription)\)\s*$/i, "")
            .replace(/\s+-\s+Default Title\s*$/i, "")
            .split(/\s+\|\s+/)[0]
            .trim();
    }


    /* ============================================================
       GRID LAYOUT
       ------------------------------------------------------------
       Legacy dynamic column token retained for compatibility.

       Final visual capacity is controlled by CSS:
       5 columns on desktop and 4 columns on mobile.
       ============================================================ */

    function getGridColumnCount(totalItems) {
        const count = Math.max(
            1,
            Number(totalItems) || 1
        );

        return Math.min(
            count,
            5
        );
    }


    function updateGridColumns(section) {
        if (!section) {
            return;
        }

        const grid = section.querySelector(".ssl-grid");

        if (!grid) {
            return;
        }

        const count = grid.querySelectorAll(".ssl-item").length;

        section.style.setProperty(
            "--ssl-gift-columns",
            String(
                getGridColumnCount(count)
            )
        );
    }


    /* ============================================================
       PLAN HELPERS
       ============================================================ */

    function findPlanCard(element) {
        if (!element) {
            return null;
        }

        return (
            element.closest(PLAN_SELECTOR) ||
            element.closest(".subscription-plan-wrapper.recurring") ||
            element.closest("[sss-selling-plan-id]") ||
            null
        );
    }


    function normalizeSellingPlanId(value) {
        const text = clean(value);

        if (!text) {
            return "";
        }

        const gidMatch = text.match(
            /SellingPlan(?:\\+\/|\/)(\d{5,})/i
        );

        if (gidMatch) {
            return gidMatch[1];
        }

        if (/^\d{5,}$/.test(text)) {
            return text;
        }

        return "";
    }


    function getCardSellingPlanIds(card) {
        const ids = new Set();

        if (!card) {
            return ids;
        }

        const add = value => {
            const id = normalizeSellingPlanId(value);

            if (id) {
                ids.add(id);
            }
        };


        Array.from(
            card.attributes || []
        ).forEach(attribute => {
            if (
                /selling.?plan|plan.?id/i.test(
                    attribute.name
                )
            ) {
                add(attribute.value);
            }
        });


        card.querySelectorAll(`
            [sss-selling-plan-id],
            [selling-plan-id],
            [data-selling-plan-id],
            [data-plan-id]
        `).forEach(element => {
            add(
                element.getAttribute(
                    "sss-selling-plan-id"
                )
            );

            add(
                element.getAttribute(
                    "selling-plan-id"
                )
            );

            add(
                element.getAttribute(
                    "data-selling-plan-id"
                )
            );

            add(
                element.getAttribute(
                    "data-plan-id"
                )
            );
        });


        card.querySelectorAll(
            'input[name*="selling_plan"], select[name*="selling_plan"]'
        ).forEach(element => {
            add(element.value);
        });


        const html = card.outerHTML || "";

        [
            /sss-selling-plan-id=["'](\d{5,})["']/gi,
            /data-selling-plan-id=["'](\d{5,})["']/gi,
            /selling-plan-id=["'](\d{5,})["']/gi
        ].forEach(pattern => {
            let match;

            while (
                (
                    match =
                    pattern.exec(html)
                )
            ) {
                add(match[1]);
            }
        });


        return ids;
    }


    /* ============================================================
       SUPER FREE SHIPPING CONFIGURATION
       ============================================================ */

    function getFreeShippingRecords() {
        const records = [];

        const booleanPattern =
            /["']?freeShipping["']?\s*:\s*(true|false)/gi;


        Array.from(
            document.scripts
        ).forEach(script => {
            const text =
                script.textContent || "";

            if (!/freeShipping/i.test(text)) {
                return;
            }

            let match;

            while (
                (
                    match =
                    booleanPattern.exec(text)
                )
            ) {
                const enabled =
                    match[1].toLowerCase() ===
                    "true";

                const flagIndex =
                    match.index;

                const start =
                    Math.max(
                        0,
                        flagIndex - 1800
                    );

                const end =
                    Math.min(
                        text.length,
                        flagIndex + 2200
                    );

                const region =
                    text.slice(
                        start,
                        end
                    );

                const candidates = [];

                const gidPattern =
                    /SellingPlan(?:\\+\/|\/)(\d{5,})/gi;

                let gidMatch;

                while (
                    (
                        gidMatch =
                        gidPattern.exec(region)
                    )
                ) {
                    const absoluteIndex =
                        start +
                        gidMatch.index;

                    candidates.push({
                        id:
                            gidMatch[1],

                        distance:
                            Math.abs(
                                absoluteIndex -
                                flagIndex
                            ),

                        after:
                            absoluteIndex >
                            flagIndex
                    });
                }


                const explicitPattern =
                    /(?:sellingPlanId|selling_plan_id|selling-plan-id)["']?\s*[:=]\s*["']?(\d{5,})/gi;

                let explicitMatch;

                while (
                    (
                        explicitMatch =
                        explicitPattern.exec(region)
                    )
                ) {
                    const absoluteIndex =
                        start +
                        explicitMatch.index;

                    candidates.push({
                        id:
                            explicitMatch[1],

                        distance:
                            Math.abs(
                                absoluteIndex -
                                flagIndex
                            ),

                        after:
                            absoluteIndex >
                            flagIndex
                    });
                }


                candidates.sort(
                    (a, b) => {
                        const difference =
                            a.distance -
                            b.distance;

                        if (
                            Math.abs(
                                difference
                            ) > 80
                        ) {
                            return difference;
                        }

                        if (
                            a.after !==
                            b.after
                        ) {
                            return a.after
                                ? -1
                                : 1;
                        }

                        return difference;
                    }
                );


                records.push({
                    enabled,

                    planId:
                        candidates[0]?.id ||
                        ""
                });
            }
        });


        const deduplicated = [];
        const seen = new Set();


        records.forEach(record => {
            const key =
                `${record.planId}::${record.enabled}`;

            if (seen.has(key)) {
                return;
            }

            seen.add(key);

            deduplicated.push(record);
        });


        return deduplicated;
    }


    function isFreeShippingEnabled(card) {
        if (!card) {
            return false;
        }

        const records =
            getFreeShippingRecords();

        if (!records.length) {
            return false;
        }

        const cardPlanIds =
            getCardSellingPlanIds(card);


        for (const record of records) {
            if (
                record.planId &&
                cardPlanIds.has(
                    record.planId
                )
            ) {
                return (
                    record.enabled ===
                    true
                );
            }
        }


        const root =
            card.closest(
                ROOT_SELECTOR
            );

        const cards =
            root
                ? Array.from(
                    root.querySelectorAll(
                        PLAN_SELECTOR
                    )
                )
                : [];


        const uniqueStates = [
            ...new Set(
                records.map(
                    record =>
                        record.enabled
                )
            )
        ];


        if (
            cards.length === 1 &&
            uniqueStates.length === 1
        ) {
            return (
                uniqueStates[0] ===
                true
            );
        }


        return false;
    }


    /* ============================================================
       DESCRIPTION / MOST POPULAR PILL
       ------------------------------------------------------------
       The text itself still comes from Super's native Description
       field.

       IMPORTANT:
       The custom pill now also inherits the EXACT computed font
       size from Super's native Description element.

       Example:
       Super Description = 16px
       MOST POPULAR       = 16px

       This means the Description font-size control in Super's
       native design panel remains the source of truth.
       ============================================================ */

    function markDescriptionOverflowHosts(card) {
        if (!card) {
            return;
        }

        let node =
            card.parentElement;

        while (
            node &&
            !node.matches?.(
                ROOT_SELECTOR
            )
        ) {
            node.classList.add(
                DESCRIPTION_OVERFLOW_CLASS
            );

            node =
                node.parentElement;
        }
    }


    function clearDescriptionPill(card) {
        if (!card) {
            return;
        }

        card.querySelectorAll(
            `.${DESCRIPTION_PILL_CLASS}`
        ).forEach(
            pill =>
                pill.remove()
        );

        card.classList.remove(
            DESCRIPTION_CARD_CLASS
        );

        card.querySelectorAll(
            `.${DESCRIPTION_SOURCE_HIDDEN_CLASS}`
        ).forEach(source => {
            source.classList.remove(
                DESCRIPTION_SOURCE_HIDDEN_CLASS
            );
        });
    }


    function enhanceDescriptionPill(card) {
        if (!card) {
            return;
        }

        const source =
            card.querySelector(
                DESCRIPTION_SELECTOR
            );

        if (!source) {
            clearDescriptionPill(card);
            return;
        }

        const descriptionText =
            rawTextOf(source);

        if (!descriptionText) {
            clearDescriptionPill(card);
            return;
        }


        /*
          Read the final native Description font size from Super.

          getComputedStyle() returns the resolved px value even if
          Super's design settings are driven by CSS variables.
        */

        const sourceStyle =
            window.getComputedStyle(
                source
            );

        const nativeDescriptionFontSize =
            clean(
                sourceStyle.fontSize
            );


        let pill =
            card.querySelector(
                `.${DESCRIPTION_PILL_CLASS}`
            );

        if (!pill) {
            pill = makeElement(
                "div",
                DESCRIPTION_PILL_CLASS
            );

            pill.setAttribute(
                "aria-hidden",
                "true"
            );

            card.appendChild(pill);
        }


        if (
            pill.textContent !==
            descriptionText
        ) {
            pill.textContent =
                descriptionText;
        }


        pill.dataset.ssfgDescription =
            descriptionText;


        /*
          Inline font-size intentionally overrides the fallback
          font-size in our CSS.

          Therefore:
          Native Super Description control = actual pill size.
        */

        if (nativeDescriptionFontSize) {
            pill.style.fontSize =
                nativeDescriptionFontSize;

            pill.dataset
                .ssfgNativeDescriptionFontSize =
                nativeDescriptionFontSize;
        }


        card.classList.add(
            DESCRIPTION_CARD_CLASS
        );

        markDescriptionOverflowHosts(card);

        source.classList.add(
            DESCRIPTION_SOURCE_HIDDEN_CLASS
        );
    }


    /* ============================================================
       DISCOUNT / SAVE BADGE
       ============================================================ */

    function parseExactPercentage(text) {
        const match =
            clean(text).match(
                /^-?\s*(\d+(?:\.\d+)?)\s*%$/
            );

        return match
            ? clean(match[1])
            : "";
    }


    function hasVisibleBackground(element) {
        if (!element) {
            return false;
        }

        const background =
            clean(
                window
                    .getComputedStyle(
                        element
                    )
                    .backgroundColor
            ).toLowerCase();

        if (
            !background ||
            background === "transparent"
        ) {
            return false;
        }

        if (
            /^rgba?\(\s*0\s*,\s*0\s*,\s*0\s*,\s*0(?:\.0+)?\s*\)$/i
                .test(background)
        ) {
            return false;
        }

        return true;
    }


    function findNativeDiscountBadge(card) {
        if (!card) {
            return null;
        }

        const candidates = [];


        card.querySelectorAll(
            "span, small, div"
        ).forEach(element => {
            if (
                element.classList.contains(
                    SAVE_BADGE_CLASS
                )
            ) {
                return;
            }

            const percentage =
                parseExactPercentage(
                    rawTextOf(element)
                );

            if (!percentage) {
                return;
            }

            let node = element;

            while (
                node &&
                node !== card &&
                card.contains(node)
            ) {
                if (
                    parseExactPercentage(
                        rawTextOf(node)
                    ) !== percentage
                ) {
                    break;
                }

                const rect =
                    node.getBoundingClientRect();

                candidates.push({
                    element:
                        node,

                    percentage,

                    hasBackground:
                        hasVisibleBackground(
                            node
                        ),

                    area:
                        Math.max(
                            rect.width,
                            0
                        ) *
                        Math.max(
                            rect.height,
                            0
                        )
                });

                node =
                    node.parentElement;
            }
        });


        if (!candidates.length) {
            return null;
        }

        candidates.sort(
            (a, b) => {
                if (
                    a.hasBackground !==
                    b.hasBackground
                ) {
                    return a.hasBackground
                        ? -1
                        : 1;
                }

                return (
                    a.area -
                    b.area
                );
            }
        );

        return candidates[0];
    }


    function getDiscountBadgePresentation(
        card,
        nativeDiscount
    ) {
        if (
            !card ||
            !nativeDiscount
        ) {
            return {
                background: "",
                color: ""
            };
        }

        const percentage =
            nativeDiscount.percentage;

        let node =
            nativeDiscount.element;

        let fallbackTextColor = "";


        while (
            node &&
            node !== card
        ) {
            if (
                parseExactPercentage(
                    rawTextOf(node)
                ) !== percentage
            ) {
                break;
            }

            const style =
                window.getComputedStyle(
                    node
                );

            if (
                !fallbackTextColor &&
                style.color
            ) {
                fallbackTextColor =
                    style.color;
            }

            if (
                hasVisibleBackground(
                    node
                )
            ) {
                return {
                    background:
                        style.backgroundColor,

                    color:
                        style.color ||
                        fallbackTextColor
                };
            }

            node =
                node.parentElement;
        }


        const candidates =
            Array.from(
                card.querySelectorAll(
                    "span, small, div"
                )
            ).filter(
                element =>
                    parseExactPercentage(
                        rawTextOf(element)
                    ) === percentage &&
                    hasVisibleBackground(
                        element
                    )
            );


        if (candidates.length) {
            const style =
                window.getComputedStyle(
                    candidates[0]
                );

            return {
                background:
                    style.backgroundColor,

                color:
                    style.color ||
                    fallbackTextColor
            };
        }


        return {
            background: "",
            color:
                fallbackTextColor
        };
    }


    function hideNativeDiscountBadge(
        card,
        percentage
    ) {
        if (
            !card ||
            !percentage
        ) {
            return;
        }

        card.querySelectorAll(
            "span, small, div"
        ).forEach(element => {
            if (
                element.classList.contains(
                    SAVE_BADGE_CLASS
                )
            ) {
                return;
            }

            if (
                parseExactPercentage(
                    rawTextOf(element)
                ) !== percentage
            ) {
                return;
            }

            element.classList.add(
                NATIVE_DISCOUNT_HIDDEN_CLASS
            );

            let parent =
                element.parentElement;

            while (
                parent &&
                parent !== card &&
                card.contains(parent)
            ) {
                if (
                    parseExactPercentage(
                        rawTextOf(parent)
                    ) !== percentage
                ) {
                    break;
                }

                parent.classList.add(
                    NATIVE_DISCOUNT_HIDDEN_CLASS
                );

                parent =
                    parent.parentElement;
            }
        });
    }


    function findSaveBenefitAnchor(
        card,
        percentage
    ) {
        if (
            !card ||
            !percentage
        ) {
            return null;
        }

        const escaped =
            escapeRegExp(
                percentage
            );

        const pattern =
            new RegExp(
                `\\bsave\\b[\\s\\S]*?${escaped}\\s*%`,
                "i"
            );


        const candidates =
            Array.from(
                card.querySelectorAll(
                    "div, span, p, li"
                )
            ).filter(element => {
                if (
                    element.closest?.(
                        `.${CUSTOM_CLASS}`
                    )
                ) {
                    return false;
                }

                if (
                    element.classList.contains(
                        SAVE_BADGE_CLASS
                    ) ||
                    element.classList.contains(
                        DESCRIPTION_PILL_CLASS
                    )
                ) {
                    return false;
                }

                const value =
                    rawTextOf(element);

                return (
                    pattern.test(value) &&
                    /\bevery\s+(?:month|order)\b/i
                        .test(value)
                );
            });


        if (!candidates.length) {
            return null;
        }


        candidates.sort(
            (a, b) =>
                rawTextOf(a).length -
                rawTextOf(b).length
        );


        return candidates[0];
    }


    function findSaveBenefitRow(
        card,
        percentage
    ) {
        const anchor =
            findSaveBenefitAnchor(
                card,
                percentage
            );

        if (!anchor) {
            return null;
        }

        const anchorText =
            rawTextOf(anchor);

        let row =
            anchor;

        let parent =
            row.parentElement;


        while (
            parent &&
            parent !== card &&
            card.contains(parent)
        ) {
            if (
                rawTextOf(parent) !==
                anchorText
            ) {
                break;
            }

            if (
                parent.querySelectorAll(
                    'input[type="radio"], [role="radio"]'
                ).length
            ) {
                break;
            }

            row =
                parent;

            parent =
                parent.parentElement;
        }


        return row;
    }


    function findBenefitsContainer(card) {
        if (!card) {
            return null;
        }

        const selectors = [
            ".sss-benefits-list",
            ".sss-aurora-benefits",
            ".sss-aurora-benefits-list",
            '[class*="benefits-list"]',
            '[class*="benefit-list"]'
        ];


        for (const selector of selectors) {
            const element =
                card.querySelector(
                    selector
                );

            if (
                element &&
                !element.closest(
                    `.${CUSTOM_CLASS}`
                )
            ) {
                return element;
            }
        }


        return null;
    }


    function placeSaveBadge(
        card,
        sourceRow,
        badge
    ) {
        if (
            !card ||
            !badge
        ) {
            return false;
        }


        if (
            sourceRow &&
            sourceRow.parentElement
        ) {
            const container =
                sourceRow.parentElement;

            const firstNativeChild =
                Array.from(
                    container.children
                ).find(
                    child =>
                        child !== badge &&
                        !child.classList.contains(
                            SAVE_BADGE_CLASS
                        )
                ) || null;


            if (firstNativeChild) {
                if (
                    badge.parentElement !==
                    container ||
                    badge.nextElementSibling !==
                    firstNativeChild
                ) {
                    container.insertBefore(
                        badge,
                        firstNativeChild
                    );
                }

                return true;
            }
        }


        const benefits =
            findBenefitsContainer(card);


        if (
            benefits &&
            benefits.parentElement
        ) {
            benefits.parentElement
                .insertBefore(
                    badge,
                    benefits
                );

            return true;
        }


        return false;
    }


    function enhanceSaveBadge(card) {
        if (!card) {
            return;
        }

        const nativeDiscount =
            findNativeDiscountBadge(card);

        if (!nativeDiscount) {
            return;
        }

        const percentage =
            nativeDiscount.percentage;

        const presentation =
            getDiscountBadgePresentation(
                card,
                nativeDiscount
            );

        const sourceRow =
            findSaveBenefitRow(
                card,
                percentage
            );


        let badge =
            card.querySelector(
                `.${SAVE_BADGE_CLASS}`
            );

        if (!badge) {
            badge = makeElement(
                "div",
                SAVE_BADGE_CLASS
            );
        }


        const desiredText =
            `${percentage}% OFF FOR LIFE`;


        if (
            badge.textContent !==
            desiredText
        ) {
            badge.textContent =
                desiredText;
        }


        badge.dataset.ssfgDiscount =
            percentage;


        badge.style.removeProperty(
            "--ssfg-save-badge-bg"
        );

        badge.style.removeProperty(
            "--ssfg-save-badge-text"
        );


        if (presentation.background) {
            badge.style.setProperty(
                "--ssfg-save-badge-bg",
                presentation.background
            );
        }

        if (presentation.color) {
            badge.style.setProperty(
                "--ssfg-save-badge-text",
                presentation.color
            );
        }


        if (
            !placeSaveBadge(
                card,
                sourceRow,
                badge
            )
        ) {
            return;
        }


        hideNativeDiscountBadge(
            card,
            percentage
        );
    }


    /* ============================================================
       MONEY HELPERS
       ============================================================ */

    const MONEY_REGEX =
        /(?:SGD|S\$|USD|US\$|AUD|A\$|CAD|C\$|MYR|RM|EUR|GBP|HKD|NZD|JPY|CNY|RMB|₩|¥|₹|฿|₱|\$|€|£)\s*\d[\d,]*(?:\.\d{1,2})?/i;

    const MONEY_GLOBAL_REGEX =
        /(?:SGD|S\$|USD|US\$|AUD|A\$|CAD|C\$|MYR|RM|EUR|GBP|HKD|NZD|JPY|CNY|RMB|₩|¥|₹|฿|₱|\$|€|£)\s*\d[\d,]*(?:\.\d{1,2})?/gi;

    const MONEY_EXACT_REGEX =
        /^(?:SGD|S\$|USD|US\$|AUD|A\$|CAD|C\$|MYR|RM|EUR|GBP|HKD|NZD|JPY|CNY|RMB|₩|¥|₹|฿|₱|\$|€|£)\s*\d[\d,]*(?:\.\d{1,2})?$/i;


    function extractValue(row) {
        const match =
            textOf(row).match(
                MONEY_REGEX
            );

        return match
            ? clean(match[0])
            : "";
    }


    function parseMoneyValue(text) {
        const value =
            clean(text);

        const match =
            value.match(
                /^(.*?)(\d[\d,]*(?:\.(\d{1,2}))?)([^\d]*)$/
            );

        if (!match) {
            return null;
        }

        const amount =
            Number(
                match[2]
                    .replace(/,/g, "")
            );

        if (
            !Number.isFinite(amount)
        ) {
            return null;
        }


        return {
            amount,

            prefix:
                match[1] || "",

            suffix:
                match[4] || "",

            decimalPlaces:
                match[3]
                    ? match[3].length
                    : 0
        };
    }


    /* ============================================================
       COMBINED GIFT TOTAL
       ------------------------------------------------------------
       Total Value includes:

       1. Every first-box free gift
       2. Every successfully resolved future Loyalty free product

       Loyalty product values are stored by Shopify in cents,
       whereas the first-box values arrive as formatted money
       strings from Super, so both are normalised before summing.
       ============================================================ */

    function getGiftTotal(
        firstBoxGifts,
        loyaltyRewards = []
    ) {
        const hasFirstBoxGifts =
            Array.isArray(
                firstBoxGifts
            ) &&
            firstBoxGifts.length > 0;

        const hasLoyaltyRewards =
            Array.isArray(
                loyaltyRewards
            ) &&
            loyaltyRewards.length > 0;


        if (
            !hasFirstBoxGifts &&
            !hasLoyaltyRewards
        ) {
            return null;
        }


        let total = 0;
        let decimalPlaces = 2;
        let foundValue = false;


        /* FIRST-BOX GIFTS */

        if (hasFirstBoxGifts) {
            for (const gift of firstBoxGifts) {
                const parsed =
                    parseMoneyValue(
                        gift.value
                    );

                if (!parsed) {
                    return null;
                }

                decimalPlaces =
                    Math.max(
                        decimalPlaces,
                        parsed.decimalPlaces || 0
                    );

                const rawQuantity =
                    Number(
                        gift.quantity
                    );

                const quantity =
                    Number.isFinite(
                        rawQuantity
                    ) &&
                    rawQuantity > 0
                        ? rawQuantity
                        : 1;

                total +=
                    parsed.amount *
                    quantity;

                foundValue = true;
            }
        }


        /* FUTURE LOYALTY REWARDS */

        if (hasLoyaltyRewards) {
            for (const reward of loyaltyRewards) {
                const cents =
                    Number(
                        reward?.originalValue
                    );

                if (
                    !Number.isFinite(cents) ||
                    cents <= 0
                ) {
                    continue;
                }

                const rawQuantity =
                    Number(
                        reward?.quantity
                    );

                const quantity =
                    Number.isFinite(
                        rawQuantity
                    ) &&
                    rawQuantity > 0
                        ? rawQuantity
                        : 1;

                total +=
                    (cents / 100) *
                    quantity;

                foundValue = true;
            }
        }


        if (!foundValue) {
            return null;
        }


        return {
            amount:
                total,

            decimalPlaces
        };
    }


    function formatAmountLikeSite(
        amount,
        samplePrice,
        decimalPlaces = 2
    ) {
        const sample =
            clean(samplePrice);

        const match =
            sample.match(
                /^(.*?)(\d[\d,]*(?:\.\d{1,2})?)([^\d]*)$/
            );

        const formattedNumber =
            Number(amount)
                .toLocaleString(
                    "en-US",
                    {
                        minimumFractionDigits:
                            decimalPlaces,

                        maximumFractionDigits:
                            decimalPlaces
                    }
                );


        if (match) {
            const prefix =
                match[1] || "";

            const suffix =
                match[3] || "";

            return (
                prefix +
                formattedNumber +
                suffix
            );
        }


        const currency =
            window.Shopify
                ?.currency
                ?.active ||
            window.Shopify
                ?.currency
                ?.current;


        if (currency) {
            try {
                return new Intl.NumberFormat(
                    undefined,
                    {
                        style:
                            "currency",

                        currency,

                        currencyDisplay:
                            "narrowSymbol",

                        minimumFractionDigits:
                            decimalPlaces,

                        maximumFractionDigits:
                            decimalPlaces
                    }
                ).format(
                    Number(amount)
                );
            } catch (_) {}
        }


        return formattedNumber;
    }


    function formatMoney(cents) {
        const amount =
            Number(
                cents || 0
            ) / 100;

        const currency =
            window.Shopify
                ?.currency
                ?.active ||
            window.Shopify
                ?.currency
                ?.current ||
            "USD";


        try {
            return new Intl.NumberFormat(
                undefined,
                {
                    style:
                        "currency",

                    currency,

                    minimumFractionDigits:
                        2,

                    maximumFractionDigits:
                        2
                }
            ).format(amount);
        } catch (_) {
            return (
                `${currency} ${amount.toFixed(2)}`
            );
        }
    }


    /* ============================================================
       CURRENT SUBSCRIPTION PRICE
       ============================================================ */

    function isExcludedPriceArea(element) {
        return !!(
            element.closest(
                GIFT_BLOCK_SELECTOR
            ) ||
            element.closest(
                `.${CUSTOM_CLASS}`
            )
        );
    }


    function hasLineThrough(
        element,
        card
    ) {
        let node = element;

        while (
            node &&
            node !== card
        ) {
            const decoration =
                clean(
                    window
                        .getComputedStyle(node)
                        .textDecorationLine
                ).toLowerCase();

            if (
                decoration.includes(
                    "line-through"
                )
            ) {
                return true;
            }

            node =
                node.parentElement;
        }


        return false;
    }


    function getDisplayedPlanPrice(card) {
        if (!card) {
            return "";
        }


        const candidates =
            Array.from(
                card.querySelectorAll(
                    "span, small, strong, b, div"
                )
            ).filter(element => {
                if (
                    isExcludedPriceArea(
                        element
                    )
                ) {
                    return false;
                }

                if (
                    element.classList.contains(
                        DESCRIPTION_PILL_CLASS
                    )
                ) {
                    return false;
                }

                if (element.closest(".ssfg-prepaid-top-price")) {
                    return false;
                }

                const value =
                    rawTextOf(element);

                if (
                    !MONEY_EXACT_REGEX.test(
                        value
                    )
                ) {
                    return false;
                }

                if (
                    hasLineThrough(
                        element,
                        card
                    )
                ) {
                    return false;
                }

                const style =
                    window.getComputedStyle(
                        element
                    );

                return (
                    (style.display !== "none" || element.closest(".ssfg-prepaid-native-price")) &&
                    style.visibility !== "hidden"
                );
            });


        if (!candidates.length) {
            return "";
        }


        candidates.sort(
            (a, b) => {
                function score(element) {
                    const style =
                        window.getComputedStyle(
                            element
                        );

                    const classes =
                        clean(
                            element.className
                        ).toLowerCase();

                    const weight =
                        parseInt(
                            style.fontWeight,
                            10
                        ) || 400;

                    let score = 0;


                    if (
                        classes.includes(
                            "price"
                        )
                    ) {
                        score += 100;
                    }

                    if (
                        classes.includes(
                            "final"
                        ) ||
                        classes.includes(
                            "sale"
                        ) ||
                        classes.includes(
                            "current"
                        )
                    ) {
                        score += 40;
                    }

                    score +=
                        Math.min(
                            weight,
                            900
                        ) / 10;

                    return score;
                }


                return (
                    score(b) -
                    score(a)
                );
            }
        );


        return rawTextOf(
            candidates[0]
        );
    }


    /* ============================================================
       PREPAID PRICE NORMALISATION FOR TOTAL VALUE BAR
       ------------------------------------------------------------
       Prevents a prepaid total such as $95.76 from being displayed
       incorrectly as "$95.76/mo".

       Example:

       3-Month Subscription
       Checkout prepaid total = $95.76
       Displayed equivalent   = $31.92/mo

       No product price, discount or selling-plan ID is hardcoded.
       ============================================================ */

    function getDisplayedComparePrice(card) {
        if (!card) {
            return "";
        }


        const candidates =
            Array.from(
                card.querySelectorAll(
                    "span, small, strong, b, div"
                )
            ).filter(element => {
                if (
                    isExcludedPriceArea(
                        element
                    )
                ) {
                    return false;
                }

                if (
                    element.classList.contains(
                        DESCRIPTION_PILL_CLASS
                    )
                ) {
                    return false;
                }

                if (element.closest(".ssfg-prepaid-top-price")) {
                    return false;
                }

                const value =
                    rawTextOf(element);

                if (
                    !MONEY_EXACT_REGEX.test(
                        value
                    )
                ) {
                    return false;
                }

                if (
                    !hasLineThrough(
                        element,
                        card
                    )
                ) {
                    return false;
                }

                const style =
                    window.getComputedStyle(
                        element
                    );

                return (
                    (style.display !== "none" || element.closest(".ssfg-prepaid-native-price")) &&
                    style.visibility !== "hidden"
                );
            });


        if (!candidates.length) {
            return "";
        }


        candidates.sort(
            (a, b) => {
                function score(element) {
                    const classes =
                        clean(
                            element.className
                        ).toLowerCase();

                    let score = 0;

                    if (
                        classes.includes(
                            "compare"
                        )
                    ) {
                        score += 120;
                    }

                    if (
                        classes.includes(
                            "price"
                        )
                    ) {
                        score += 80;
                    }

                    const rect =
                        element.getBoundingClientRect();

                    score -=
                        Math.min(
                            Math.max(
                                rect.width *
                                rect.height,
                                0
                            ),
                            5000
                        ) / 5000;

                    return score;
                }


                return (
                    score(b) -
                    score(a)
                );
            }
        );


        return rawTextOf(
            candidates[0]
        );
    }


    function getVisibleRealOneTimeCard(root) {
        if (!root) return null;

        // Super's Aurora one-time option is not always marked with
        // .subscription-plan-wrapper.one-time. Locate its visible card
        // from both the native classes and its one-time purchase label.
        const cardSelector = [
            ".subscription-plan-wrapper.one-time",
            ".sss-aurora-option.subscription-option",
            ".sss-aurora-option",
            ".subscription-plan-wrapper"
        ].join(", ");

        const isVisible = element => {
            const style = window.getComputedStyle(element);
            return style.display !== "none" && style.visibility !== "hidden";
        };

        const isOneTime = element => {
            const label = textOf(element);
            return /\bone[\s-]?time\s+purchase\b|\bbuy\s+once\b/i.test(label) &&
                !element.classList.contains("recurring") &&
                element.getAttribute("data-ssq-internal-one-time") !== "true" &&
                !element.classList.contains("top");
        };

        const candidates = Array.from(root.querySelectorAll(cardSelector))
            .filter(card => isVisible(card) && isOneTime(card));

        // Prefer a genuinely priced card rather than a text-only nested node.
        for (const card of candidates) {
            if (parseMoneyValue(getDisplayedPlanPrice(card))?.amount > 0) {
                return card;
            }
        }

        // Fallback when the Aurora card uses an unexpected wrapper class:
        // locate the actual one-time label and climb to a priced container.
        const labels = Array.from(root.querySelectorAll("label, span, strong, b, p, div"))
            .filter(el => {
                const label = rawTextOf(el);
                return label.length > 0 && label.length < 90 &&
                    /\bone[\s-]?time\s+purchase\b|\bbuy\s+once\b/i.test(label);
            });

        for (const label of labels) {
            let ancestor = label;
            for (let depth = 0; ancestor && ancestor !== root && depth < 6; depth++) {
                if (isVisible(ancestor) &&
                    parseMoneyValue(getDisplayedPlanPrice(ancestor))?.amount > 0 &&
                    !ancestor.querySelector(`.${CUSTOM_CLASS}`)) {
                    return ancestor;
                }
                ancestor = ancestor.parentElement;
            }
        }

        return null;
    }


    function getPrepaidPriceDisplayMode(card) {
        if (!card) {
            return "";
        }


        const host =
            (
                card.hasAttribute?.(
                    "data-prepaid-price-display-mode"
                )
                    ? card
                    : null
            ) ||
            card.closest?.(
                "[data-prepaid-price-display-mode]"
            ) ||
            card.querySelector?.(
                "[data-prepaid-price-display-mode]"
            ) ||
            null;


        return clean(
            host?.getAttribute(
                "data-prepaid-price-display-mode"
            ) || ""
        ).toLowerCase();
    }


    function getExplicitPrepaidDeliveryCount(card) {
        if (!card) {
            return 0;
        }


        const attributeNames = [
            "data-prepaid-delivery-count",
            "data-prepaid-deliveries",
            "data-delivery-count",
            "data-deliveries",
            "data-order-count",
            "data-box-count"
        ];


        const candidateNodes = [
            card,

            ...Array.from(
                card.querySelectorAll(
                    attributeNames
                        .map(
                            name =>
                                `[${name}]`
                        )
                        .join(",")
                )
            )
        ];


        for (const node of candidateNodes) {
            for (
                const attributeName
                of attributeNames
            ) {
                const count =
                    Number(
                        clean(
                            node.getAttribute?.(
                                attributeName
                            ) || ""
                        )
                    );

                if (
                    Number.isFinite(count) &&
                    Number.isInteger(count) &&
                    count >= 2
                ) {
                    return count;
                }
            }
        }


        return 0;
    }


    function getTextBasedPrepaidDeliveryCount(card) {
        if (!card) {
            return 0;
        }


        /*
          Use the explicit month duration written in the plan.

          Examples:
          3-Month Subscription -> 3
          6 Month Subscription -> 6

          We deliberately do NOT infer a delivery count from copy
          such as "ORDER 3", because Loyalty rewards also contain
          order numbers.
        */

        const text =
            textOf(card);

        if (!text) {
            return 0;
        }


        const monthMatch =
            text.match(
                /\b(\d{1,2})\s*[-–—]?\s*months?\b/i
            );


        if (!monthMatch) {
            return 0;
        }


        const count =
            Number(
                monthMatch[1]
            );


        return (
            Number.isFinite(count) &&
            count >= 2
        )
            ? count
            : 0;
    }


    function getPrepaidDeliveryCount(card, oneTimeParsed, compareParsed) {
        /*
          A "3-Month Subscription" title describes its duration, NOT
          whether three months are paid upfront. Use native Super prices
          against the regular one-time product price to classify billing.
          This must be shared by the top-right and black-bar prices.

          Return values:
            >= 2 = prepaid full-period total, divide by that count
               0 = normal monthly amount, never divide
              -1 = unresolved, do not fabricate a monthly price
        */
        const count = getExplicitPrepaidDeliveryCount(card) ||
            getTextBasedPrepaidDeliveryCount(card);

        if (count < 2) return 0;

        if (oneTimeParsed?.amount > 0 && compareParsed?.amount > 0) {
            const ratio = compareParsed.amount / oneTimeParsed.amount;
            const near = target => Math.abs(ratio - target) <=
                Math.max(0.02, target * 0.015);

            // Check monthly first so a stale "total" hint cannot divide it.
            if (near(1)) return 0;
            if (near(count)) return count;
        }

        // Only explicit native metadata can break an inconclusive tie.
        const mode = getPrepaidPriceDisplayMode(card);
        if (mode === "total") return count;
        if (["monthly", "per_month", "per-month", "unit", "per_delivery"]
            .includes(mode)) return 0;

        return -1;
    }


    function getTotalPricePresentation(card, planPrice) {
        const fallback = {
            value: planPrice || "",
            cycle: "", // Never label an unverified total as a monthly price.
            prepaidDeliveries: 0
        };
        if (!card || !planPrice) return fallback;

        const planParsed = parseMoneyValue(planPrice);
        if (!planParsed || planParsed.amount <= 0) return fallback;

        const root = card.closest(ROOT_SELECTOR);
        const oneTimeCard = getVisibleRealOneTimeCard(root);
        const oneTimeParsed = parseMoneyValue(getDisplayedPlanPrice(oneTimeCard));
        const compareParsed = parseMoneyValue(getDisplayedComparePrice(card));
        const count = getPrepaidDeliveryCount(card, oneTimeParsed, compareParsed);
        const cycle = getVerifiedDeliveryCycle(card);

        if (count >= 2) {
            return {
                value: formatAmountLikeSite(
                    planParsed.amount / count, planPrice,
                    Math.max(planParsed.decimalPlaces || 0, 2)
                ),
                cycle,
                prepaidDeliveries: count
            };
        }

        if (count === 0) {
            return { value: planPrice, cycle, prepaidDeliveries: 1 };
        }

        // Unknown billing mode: retain native value without a false /mo.
        return fallback;
    }


    /* ============================================================
       MERCHANDISE VALUE FOR THE TOTAL VALUE BAR — DISPLAY ONLY
       ------------------------------------------------------------
       Count delivered units from Shopify's actual billing and
       delivery policies, never from the subscription option name.
       Use the product's native regular one-time price as the base;
       do not use a potentially inflated compare-at MSRP.

       If the schedule or regular price cannot be verified, leave
       the gift-only value in place rather than invent a product
       quantity or value. No checkout or selling-plan data changes.
       ============================================================ */

    function getVerifiedPaidDeliveryCount(card) {
        const policies = getVerifiedSellingPlanPolicies(card);
        const billing = policies?.billingPolicy;
        const delivery = policies?.deliveryPolicy;

        if (
            billing?.__typename !== "SellingPlanRecurringBillingPolicy" ||
            delivery?.__typename !== "SellingPlanRecurringDeliveryPolicy"
        ) {
            return 0;
        }

        const billingCount = Number(billing.intervalCount);
        const deliveryCount = Number(delivery.intervalCount);
        const billingUnit = billing.interval;
        const deliveryUnit = delivery.interval;
        const scale = { DAY: 1, WEEK: 7, MONTH: 1, YEAR: 12 };

        if (
            !Number.isSafeInteger(billingCount) || billingCount < 1 ||
            !Number.isSafeInteger(deliveryCount) || deliveryCount < 1 ||
            !(billingUnit in scale) || !(deliveryUnit in scale)
        ) {
            return 0;
        }

        // Only exact unit conversions: days/weeks, months/years,
        // or identical units. A month is not assumed to be 30 days.
        const sameFamily = (
            billingUnit === deliveryUnit ||
            (["DAY", "WEEK"].includes(billingUnit) &&
             ["DAY", "WEEK"].includes(deliveryUnit)) ||
            (["MONTH", "YEAR"].includes(billingUnit) &&
             ["MONTH", "YEAR"].includes(deliveryUnit))
        );
        if (!sameFamily) return 0;

        const count = (billingCount * scale[billingUnit]) /
            (deliveryCount * scale[deliveryUnit]);

        return Number.isSafeInteger(count) && count >= 1 && count <= 100
            ? count
            : 0;
    }

    function getVerifiedSubscriptionProductValue(card) {
        if (!card) return null;

        const coveredDeliveries = getVerifiedPaidDeliveryCount(card);
        if (!coveredDeliveries) return null;

        const oneTimeCard = getVisibleRealOneTimeCard(card.closest(ROOT_SELECTOR));
        const regularPrice = parseMoneyValue(getDisplayedPlanPrice(oneTimeCard));
        if (!regularPrice || regularPrice.amount <= 0) return null;

        // Super's undiscounted price is only used as a consistency
        // check, never as a higher retail-price substitute.
        const comparePrice = parseMoneyValue(getDisplayedComparePrice(card));
        const computedAmount = regularPrice.amount * coveredDeliveries;

        if (comparePrice?.amount > 0) {
            const closeTo = expected =>
                Math.abs(comparePrice.amount - expected) <=
                Math.max(0.02, expected * 0.001);

            // Super may show a per-delivery or full-prepaid original.
            // If it matches neither, don't claim an unverified value.
            if (!closeTo(regularPrice.amount) && !closeTo(computedAmount)) {
                return null;
            }
        }

        return {
            amount: computedAmount,
            decimalPlaces: Math.max(2, regularPrice.decimalPlaces || 0)
        };
    }

    function getCombinedOfferValue(firstBoxGifts, loyaltyRewards, card) {
        const gifts = getGiftTotal(firstBoxGifts, loyaltyRewards);
        const product = getVerifiedSubscriptionProductValue(card);
        if (!gifts && !product) return null;

        const hasFirstBox = Array.isArray(firstBoxGifts) &&
            firstBoxGifts.length > 0;
        const hasLoyalty = Array.isArray(loyaltyRewards) &&
            loyaltyRewards.some(reward => Number(reward?.originalValue) > 0);

        const parts = [];
        if (product) parts.push("PRODUCTS");
        if (hasFirstBox && gifts) parts.push("FIRST-BOX GIFTS");
        if (hasLoyalty && gifts) parts.push("LOYALTY GIFTS");

        return {
            amount: (gifts?.amount || 0) + (product?.amount || 0),
            decimalPlaces: Math.max(
                2, gifts?.decimalPlaces || 0, product?.decimalPlaces || 0
            ),
            subtitle: parts.join(" + ")
                .replace("FIRST-BOX GIFTS + LOYALTY GIFTS",
                    "FIRST-BOX & LOYALTY GIFTS")
        };
    }


    /* ============================================================
       TOP-RIGHT PREPAID PRICE — DISPLAY ONLY
       Uses Super's native total and compare-at prices, never a
       hardcoded price. Does not change selling plans or checkout.
       ============================================================ */

    function findNativePriceNode(card, crossedOut) {
        const nodes = Array.from(card.querySelectorAll("span, small, strong, b, div"))
            .filter(el => {
                if (el.closest(".ssfg-prepaid-top-price") || isExcludedPriceArea(el)) return false;
                if (!MONEY_EXACT_REGEX.test(rawTextOf(el))) return false;
                if (hasLineThrough(el, card) !== crossedOut) return false;
                return true;
            });
        nodes.sort((a, b) => {
            const score = el => {
                const cls = String(el.className || "").toLowerCase();
                return (cls.includes("price") ? 100 : 0) +
                    (cls.includes("compare") === crossedOut ? 40 : 0) -
                    Math.min(el.getBoundingClientRect().width, 1000) / 10000;
            };
            return score(b) - score(a);
        });
        return nodes[0] || null;
    }

    /* ============================================================
       VERIFIED STOREFRONT BILLING SCHEDULE — DISCLOSURE ONLY
       ------------------------------------------------------------
       The storefront's own Storefront API supplies the actual
       billing interval. Never derive billing frequency from plan
       names, delivery counts, or product prices.

       One request per product page; selling-plan IDs and prices
       are discovered dynamically for each Shopify store.
       A failed/ambiguous lookup hides ONLY the disclosure.
       ============================================================ */

    const ssfgBillingPolicyCache = new Map();

    function getStorefrontBillingPolicyRecord() {
        const handle = getHandleFromUrl(window.location.pathname);
        if (!handle) return null;

        // This cache lives within the current store's page only.
        const cacheKey = `${window.location.origin}|${handle}`;
        if (ssfgBillingPolicyCache.has(cacheKey)) {
            return ssfgBillingPolicyCache.get(cacheKey);
        }

        const record = { status: "loading", byId: new Map() };
        ssfgBillingPolicyCache.set(cacheKey, record);

        const query = `
            query SSFGBillingSchedule($handle: String!) {
                product(handle: $handle) {
                    sellingPlanGroups(first: 20) {
                        nodes {
                            sellingPlans(first: 100) {
                                nodes {
                                    id
                                    billingPolicy {
                                        __typename
                                        ... on SellingPlanRecurringBillingPolicy {
                                            interval
                                            intervalCount
                                        }
                                    }
                                    deliveryPolicy {
                                        __typename
                                        ... on SellingPlanRecurringDeliveryPolicy {
                                            interval
                                            intervalCount
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        `;

        fetch("/api/2026-10/graphql.json", {
            method: "POST",
            credentials: "same-origin",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ query, variables: { handle } })
        })
            .then(async response => {
                if (!response.ok) throw new Error(`HTTP ${response.status}`);
                const result = await response.json();
                if (result.errors?.length || !result.data?.product) {
                    throw new Error("Selling-plan billing policies unavailable");
                }

                const groups = result.data.product.sellingPlanGroups?.nodes || [];
                for (const group of groups) {
                    for (const plan of group.sellingPlans?.nodes || []) {
                        const id = normalizeSellingPlanId(plan.id);
                        if (id) {
                            record.byId.set(id, {
                                billingPolicy: plan.billingPolicy || null,
                                deliveryPolicy: plan.deliveryPolicy || null
                            });
                        }
                    }
                }
                record.status = "ready";
            })
            .catch(() => {
                // Do not manufacture a renewal interval on API failure.
                record.status = "unavailable";
            })
            .finally(() => {
                // Super's original prices and every other widget area stay intact.
                scheduleRefresh();
            });

        return record;
    }

    function getVerifiedSellingPlanPolicies(card) {
        const ids = getCardSellingPlanIds(card);
        if (!ids.size) return null;

        const record = getStorefrontBillingPolicyRecord();
        if (!record || record.status !== "ready") return null;

        const matched = [...ids].filter(id => record.byId.has(id));
        if (matched.length !== 1) return null;

        return record.byId.get(matched[0]) || null;
    }

    /* The amount is unchanged; only the verified delivery unit changes.
       Preserve /mo unless Shopify explicitly confirms a different cadence. */
    function getVerifiedDeliveryCycle(card) {
        const policy = getVerifiedSellingPlanPolicies(card)?.deliveryPolicy;
        if (policy?.__typename !== "SellingPlanRecurringDeliveryPolicy") {
            return "/mo";
        }

        const count = Number(policy.intervalCount);
        if (!Number.isSafeInteger(count) || count < 1 ||
            !["DAY", "WEEK", "MONTH", "YEAR"].includes(policy.interval)) {
            return "/mo";
        }

        return policy.interval === "MONTH" && count === 1
            ? "/mo"
            : "/delivery";
    }

    function getVerifiedBillingDisclosure(card, nativeCurrent) {
        const policy = getVerifiedSellingPlanPolicies(card)?.billingPolicy;
        if (policy?.__typename !== "SellingPlanRecurringBillingPolicy") {
            // No claim of recurring billing for fixed/unknown policies.
            return "";
        }

        const units = {
            DAY: "day",
            WEEK: "week",
            MONTH: "month",
            YEAR: "year"
        };
        const unit = units[policy.interval];
        const count = Number(policy.intervalCount);
        if (!unit || !Number.isSafeInteger(count) || count < 1) return "";

        const duration = count === 1 ? unit : `${count} ${unit}s`;
        return `${nativeCurrent} billed every ${duration}`;
    }

    function enhancePrepaidTopPrice(card) {
        if (!card) return;
        const count = getExplicitPrepaidDeliveryCount(card) ||
            getTextBasedPrepaidDeliveryCount(card);
        if (count < 2 || !/subscription/i.test(textOf(card))) return;

        let overlay = card.querySelector(".ssfg-prepaid-top-price");
        const currentNode = findNativePriceNode(card, false);
        const compareNode = findNativePriceNode(card, true);
        if (!currentNode || !compareNode) return;

        const nativeCurrent = rawTextOf(currentNode);
        const nativeCompare = rawTextOf(compareNode);
        const currentParsed = parseMoneyValue(nativeCurrent);
        const compareParsed = parseMoneyValue(nativeCompare);
        if (!currentParsed || !compareParsed || currentParsed.amount <= 0 ||
            compareParsed.amount <= currentParsed.amount) return;

        // Use EXACTLY the same decision as the black "ONLY" value bar.
        const root = card.closest(ROOT_SELECTOR);
        const oneTimeCard = getVisibleRealOneTimeCard(root);
        const oneTimeParsed = parseMoneyValue(getDisplayedPlanPrice(oneTimeCard));
        const prepaidCount = getPrepaidDeliveryCount(
            card, oneTimeParsed, compareParsed
        );

        if (prepaidCount < 0) {
            // Safe fallback: expose Super's own native price, not a
            // fabricated /mo price from an unknown billing arrangement.
            overlay?.remove();
            card.querySelectorAll(".ssfg-prepaid-native-price").forEach(el =>
                el.classList.remove("ssfg-prepaid-native-price")
            );
            card.classList.remove("ssfg-prepaid-price-card");
            return;
        }

        // Identify the smallest price-only container; never hide the title.
        let host = currentNode.parentElement;
        while (host && host !== card && !host.contains(compareNode)) {
            host = host.parentElement;
        }
        if (!host || host === card) return;
        if (host.querySelector('input, button, [role="radio"]')) return;
        if (/subscription|ships monthly|one.time purchase/i.test(rawTextOf(host))) return;

        const divisor = prepaidCount >= 2 ? prepaidCount : 1;
        const original = formatAmountLikeSite(
            compareParsed.amount / divisor, nativeCompare, 2
        );
        const monthly = formatAmountLikeSite(
            currentParsed.amount / divisor, nativeCurrent, 2
        );
        const disclosure = prepaidCount >= 2
            ? getVerifiedBillingDisclosure(card, nativeCurrent)
            : "";
        const cycle = getVerifiedDeliveryCycle(card);
        const signature = [original, monthly, disclosure, cycle].join("|");
        const currentStyle = window.getComputedStyle(currentNode);

        if (!overlay) {
            overlay = makeElement("div", "ssfg-prepaid-top-price");
            overlay.innerHTML = '<div class="ssfg-prepaid-top-original"></div>' +
                '<div class="ssfg-prepaid-top-line"><span class="ssfg-prepaid-top-current"></span>' +
                '<span class="ssfg-prepaid-top-cycle">/mo</span></div>' +
                '<div class="ssfg-prepaid-top-disclosure"></div>';
        }
        if (overlay.parentElement !== host) host.appendChild(overlay);
        if (overlay.dataset.signature !== signature) {
            overlay.querySelector(".ssfg-prepaid-top-original").textContent = `${original}${cycle}`;
            overlay.querySelector(".ssfg-prepaid-top-current").textContent = monthly;
            overlay.querySelector(".ssfg-prepaid-top-cycle").textContent = cycle;
            const disclosureNode = overlay.querySelector(".ssfg-prepaid-top-disclosure");
            disclosureNode.textContent = disclosure;
            disclosureNode.hidden = !disclosure;
            overlay.dataset.signature = signature;
        }
        overlay.style.fontSize = currentStyle.fontSize;
        overlay.style.fontWeight = currentStyle.fontWeight;
        overlay.style.color = currentStyle.color;
        for (const child of Array.from(host.children)) {
            if (child !== overlay) child.classList.add("ssfg-prepaid-native-price");
        }
        card.classList.add("ssfg-prepaid-price-card");
    }


    /* ============================================================
       IMAGE HELPERS
       ============================================================ */

    function getBestImageUrl(image) {
        if (!image) {
            return "";
        }

        const srcset =
            clean(
                image.getAttribute(
                    "srcset"
                ) || ""
            );


        if (srcset) {
            const candidates =
                srcset
                    .split(",")
                    .map(entry => {
                        const parts =
                            clean(entry)
                                .split(/\s+/);

                        const url =
                            parts[0] || "";

                        const descriptor =
                            parts[1] || "";

                        let score = 0;


                        if (
                            /\d+w$/i.test(
                                descriptor
                            )
                        ) {
                            score =
                                parseInt(
                                    descriptor,
                                    10
                                ) || 0;
                        } else if (
                            /\d+(?:\.\d+)?x$/i.test(
                                descriptor
                            )
                        ) {
                            score =
                                (
                                    parseFloat(
                                        descriptor
                                    ) || 0
                                ) * 1000;
                        }


                        return {
                            url,
                            score
                        };
                    })
                    .filter(
                        item =>
                            item.url
                    )
                    .sort(
                        (a, b) =>
                            b.score -
                            a.score
                    );


            if (candidates.length) {
                return safeUrl(
                    candidates[0].url
                );
            }
        }


        return safeUrl(
            image.currentSrc ||
            image.getAttribute("src") ||
            ""
        );
    }


    /* ============================================================
       FIRST-BOX FREE-GIFT DISCOVERY
       ============================================================ */

    function getPlanKey(block) {
        return clean(
            block.getAttribute(
                "data-gift-block-id"
            ) ||
            block.getAttribute(
                "data-plan-id"
            ) ||
            block.getAttribute(
                "selling-plan-id"
            ) ||
            Array.from(
                block.querySelectorAll(
                    GIFT_ROW_SELECTOR
                )
            )
                .map(
                    row =>
                        row.getAttribute(
                            "data-gift-variant-id"
                        ) || ""
                )
                .filter(Boolean)
                .join("-")
        );
    }


    function getEligibleRows(block) {
        const unavailableLabel =
            clean(
                block.getAttribute(
                    "data-gift-unavailable-label"
                ) ||
                "Unavailable"
            );


        return Array.from(
            block.querySelectorAll(
                GIFT_ROW_SELECTOR
            )
        ).filter(row => {
            const recurrence =
                clean(
                    row.getAttribute(
                        "data-gift-recurrence"
                    ) || ""
                ).toLowerCase();


            if (
                recurrence &&
                recurrence !== "first_order"
            ) {
                return false;
            }


            if (unavailableLabel) {
                const unavailablePattern =
                    new RegExp(
                        `\\b${escapeRegExp(
                            unavailableLabel
                        )}\\b`,
                        "i"
                    );

                if (
                    unavailablePattern.test(
                        textOf(row)
                    )
                ) {
                    return false;
                }
            }


            return true;
        });
    }


    function deriveProductTitle(
        row,
        image,
        freeLabel
    ) {
        let value =
            textOf(row)
                .replace(
                    MONEY_GLOBAL_REGEX,
                    " "
                );


        if (freeLabel) {
            value =
                value.replace(
                    new RegExp(
                        `\\b${escapeRegExp(
                            freeLabel
                        )}\\b\\s*$`,
                        "i"
                    ),
                    " "
                );
        }


        value = clean(value);


        if (
            !value ||
            /^(?:gift|free product|product)$/i
                .test(value)
        ) {
            value =
                clean(
                    image?.getAttribute(
                        "alt"
                    ) || ""
                );
        }


        return value;
    }


    function extractGift(
        row,
        block
    ) {
        const image =
            row.querySelector("img");

        const imageUrl =
            getBestImageUrl(image);

        const value =
            extractValue(row);

        const freeLabel =
            clean(
                block.getAttribute(
                    "data-gift-free-label"
                ) ||
                "Free"
            );

        const productTitle =
            deriveProductTitle(
                row,
                image,
                freeLabel
            );

        const displayName =
            displayNameFromTitle(
                productTitle
            );


        if (
            !imageUrl ||
            !value ||
            !displayName
        ) {
            return null;
        }


        return {
            variantId:
                clean(
                    row.getAttribute(
                        "data-gift-variant-id"
                    ) || ""
                ),

            quantity:
                clean(
                    row.getAttribute(
                        "data-gift-quantity"
                    ) || "1"
                ),

            recurrence:
                clean(
                    row.getAttribute(
                        "data-gift-recurrence"
                    ) || ""
                ),

            imageUrl,

            value,

            productTitle,

            displayName
        };
    }


    /* ============================================================
       SHOPIFY PRODUCT RESOLUTION
       ============================================================ */

    function candidateScore(
        sourceTitle,
        productTitle
    ) {
        const source =
            normalise(
                sourceTitle
            );

        const candidate =
            normalise(
                productTitle
            );


        if (
            !source ||
            !candidate
        ) {
            return 0;
        }


        if (
            source ===
            candidate
        ) {
            return 100;
        }


        if (
            source.includes(
                candidate
            ) ||
            candidate.includes(
                source
            )
        ) {
            const shorter =
                Math.min(
                    source.length,
                    candidate.length
                );

            const longer =
                Math.max(
                    source.length,
                    candidate.length
                );

            return (
                80 +
                (
                    shorter /
                    longer
                ) * 10
            );
        }


        const sourceTokens =
            new Set(
                source
                    .split(" ")
                    .filter(Boolean)
            );

        const candidateTokens =
            new Set(
                candidate
                    .split(" ")
                    .filter(Boolean)
            );


        let intersection = 0;


        sourceTokens.forEach(token => {
            if (
                candidateTokens.has(
                    token
                )
            ) {
                intersection += 1;
            }
        });


        const denominator =
            Math.max(
                sourceTokens.size,
                candidateTokens.size,
                1
            );


        return (
            intersection /
            denominator
        ) * 70;
    }


    function findMatchingProduct(
        products,
        title
    ) {
        if (
            !Array.isArray(products) ||
            !products.length
        ) {
            return null;
        }


        const ranked =
            products
                .map(product => ({
                    product,

                    score:
                        candidateScore(
                            title,
                            product?.title || ""
                        )
                }))
                .sort(
                    (a, b) =>
                        b.score -
                        a.score
                );


        if (!ranked.length) {
            return null;
        }


        return (
            ranked[0].score >= 45
                ? ranked[0].product
                : null
        );
    }


    async function searchProduct(title) {
        const cacheKey =
            normalise(title);


        if (
            searchCache.has(
                cacheKey
            )
        ) {
            return searchCache.get(
                cacheKey
            );
        }


        const shopRoot =
            window.Shopify
                ?.routes
                ?.root ||
            "/";


        const params =
            new URLSearchParams();

        params.set(
            "q",
            title
        );

        params.set(
            "resources[type]",
            "product"
        );

        params.set(
            "resources[limit]",
            "10"
        );

        params.set(
            "resources[options][unavailable_products]",
            "show"
        );


        const promise =
            fetch(
                `${shopRoot}search/suggest.json?${params.toString()}`,
                {
                    credentials:
                        "same-origin",

                    headers: {
                        Accept:
                            "application/json"
                    }
                }
            )
                .then(async response => {
                    if (!response.ok) {
                        return null;
                    }

                    const data =
                        await response.json();

                    const products =
                        data
                            ?.resources
                            ?.results
                            ?.products ||
                        [];

                    return findMatchingProduct(
                        products,
                        title
                    );
                })
                .catch(
                    () => null
                );


        searchCache.set(
            cacheKey,
            promise
        );


        return promise;
    }


    async function loadProductCatalog() {
        if (catalogCache.promise) {
            return catalogCache.promise;
        }


        const shopRoot =
            window.Shopify
                ?.routes
                ?.root ||
            "/";


        catalogCache.promise =
            fetch(
                `${shopRoot}collections/all/products.json?limit=250`,
                {
                    credentials:
                        "same-origin",

                    headers: {
                        Accept:
                            "application/json"
                    }
                }
            )
                .then(async response => {
                    if (!response.ok) {
                        return [];
                    }

                    const data =
                        await response.json();

                    return Array.isArray(
                        data?.products
                    )
                        ? data.products
                        : [];
                })
                .catch(
                    () => []
                );


        return catalogCache.promise;
    }


    async function findShopifyProduct(title) {
        const predictive =
            await searchProduct(title);

        if (predictive) {
            return predictive;
        }

        const catalog =
            await loadProductCatalog();

        return findMatchingProduct(
            catalog,
            title
        );
    }


    async function fetchProduct(handle) {
        if (
            productCache.has(
                handle
            )
        ) {
            return productCache.get(
                handle
            );
        }


        const shopRoot =
            window.Shopify
                ?.routes
                ?.root ||
            "/";


        const promise =
            fetch(
                `${shopRoot}products/${encodeURIComponent(handle)}.js`,
                {
                    credentials:
                        "same-origin",

                    headers: {
                        Accept:
                            "application/json"
                    }
                }
            ).then(async response => {
                if (!response.ok) {
                    throw new Error(
                        `Could not load product ${handle}: ${response.status}`
                    );
                }

                return response.json();
            });


        productCache.set(
            handle,
            promise
        );


        return promise;
    }


    function getProductImage(product) {
        if (
            typeof product
                ?.featured_image ===
            "string"
        ) {
            return safeUrl(
                product.featured_image
            );
        }


        if (
            typeof product
                ?.featured_image
                ?.src ===
            "string"
        ) {
            return safeUrl(
                product.featured_image.src
            );
        }


        if (
            Array.isArray(
                product?.images
            ) &&
            product.images.length
        ) {
            const first =
                product.images[0];

            if (
                typeof first ===
                "string"
            ) {
                return safeUrl(first);
            }

            if (first?.src) {
                return safeUrl(
                    first.src
                );
            }
        }


        return "";
    }


    function getOriginalProductValue(product) {
        const normalPrice =
            Number(
                product?.price ||
                0
            );

        let compareAt = 0;


        if (
            Array.isArray(
                product?.variants
            )
        ) {
            product.variants
                .forEach(variant => {
                    const value =
                        Number(
                            variant
                                ?.compare_at_price ||
                            0
                        );

                    if (
                        value >
                        compareAt
                    ) {
                        compareAt =
                            value;
                    }
                });
        }


        return (
            compareAt >
            normalPrice
        )
            ? compareAt
            : normalPrice;
    }


    async function resolveLoyaltyReward(reward) {
        const match =
            await findShopifyProduct(
                reward.productTitle
            );


        if (!match) {
            throw new Error(
                `Could not find Shopify product for loyalty reward "${reward.productTitle}".`
            );
        }


        const handle =
            match.handle ||
            getHandleFromUrl(
                match.url
            );


        if (!handle) {
            throw new Error(
                `Could not determine Shopify handle for loyalty reward "${reward.productTitle}".`
            );
        }


        const product =
            await fetchProduct(
                handle
            );

        const imageUrl =
            getProductImage(
                product
            );

        const originalValue =
            getOriginalProductValue(
                product
            );


        if (
            !imageUrl ||
            !originalValue
        ) {
            throw new Error(
                `Incomplete Shopify product data for loyalty reward "${reward.productTitle}".`
            );
        }


        return {
            ...reward,

            handle,

            product,

            imageUrl,

            originalValue,

            displayName:
                displayNameFromTitle(
                    product?.title ||
                    reward.displayName ||
                    reward.productTitle
                )
        };
    }


    /* ============================================================
       LOYALTY REWARD DISCOVERY
       ============================================================ */

    function getNativeLoyaltyBlocks(root) {
        if (!root) {
            return [];
        }

        return Array.from(
            root.querySelectorAll(
                LOYALTY_SELECTOR
            )
        );
    }


    function getLoyaltyPlanId(block) {
        return clean(
            block?.getAttribute(
                "sss-selling-plan-id"
            ) ||
            block?.getAttribute(
                "selling-plan-id"
            ) ||
            block?.getAttribute(
                "data-selling-plan-id"
            ) ||
            ""
        );
    }


    function isSuperLoyaltyHidden(block) {
        if (!block) {
            return true;
        }

        return (
            block.classList.contains(
                "hidden"
            ) ||
            block.hasAttribute(
                "hidden"
            )
        );
    }


    function findLoyaltyBlockForCard(
        root,
        card
    ) {
        const blocks =
            getNativeLoyaltyBlocks(
                root
            );

        if (!blocks.length) {
            return null;
        }


        const cardPlanIds =
            getCardSellingPlanIds(
                card
            );


        const exactMatches =
            blocks.filter(block => {
                const id =
                    getLoyaltyPlanId(
                        block
                    );

                return (
                    id &&
                    cardPlanIds.has(id)
                );
            });


        if (exactMatches.length) {
            return (
                exactMatches.find(
                    block =>
                        !isSuperLoyaltyHidden(
                            block
                        )
                ) ||
                exactMatches[0]
            );
        }


        const visible =
            blocks.filter(
                block =>
                    !isSuperLoyaltyHidden(
                        block
                    )
            );


        if (visible.length === 1) {
            return visible[0];
        }


        if (blocks.length === 1) {
            return blocks[0];
        }


        return null;
    }


    function getRewardNodes(
        block,
        rewardIndex
    ) {
        return Array.from(
            block.querySelectorAll(
                "[data-lb-reward-idx]"
            )
        ).filter(
            element =>
                clean(
                    element.getAttribute(
                        "data-lb-reward-idx"
                    )
                ) ===
                String(rewardIndex)
        );
    }


    function cleanRewardProductTitle(value) {
        let title =
            clean(value);


        if (!title) {
            return "";
        }


        title =
            title
                .replace(
                    /\bFree product\b/ig,
                    " "
                )
                .replace(
                    /\bOrder\s*#?\s*\d+\b/ig,
                    " "
                );


        const freeMatch =
            title.match(
                /\bFREE\b/i
            );


        if (freeMatch) {
            title =
                title.slice(
                    freeMatch.index +
                    freeMatch[0].length
                );
        }


        title =
            clean(title)
                .replace(
                    /^[🎁\s:–—-]+/u,
                    ""
                )
                .replace(
                    /\s*\((?:one[- ]?time|subscription)\)\s*$/i,
                    ""
                )
                .replace(
                    /\s+-\s+Default Title\s*$/i,
                    ""
                )
                .trim();


        if (
            !title ||
            /^(?:free|gift|product|free product)$/i
                .test(title)
        ) {
            return "";
        }


        return title;
    }


    function extractRewardOrder(nodes) {
        for (const node of nodes) {
            const match =
                textOf(node)
                    .match(
                        /\bOrder\s*#?\s*(\d+)/i
                    );

            if (match) {
                const order =
                    Number(match[1]);

                if (
                    Number.isFinite(
                        order
                    )
                ) {
                    return order;
                }
            }
        }


        return null;
    }


    function extractRewardProductTitle(nodes) {
        const orderedNodes = [
            ...nodes.filter(
                node =>
                    node.matches?.(
                        ".sss-lb-roadmap-detail, [class*='roadmap-detail']"
                    )
            ),

            ...nodes.filter(
                node =>
                    !node.matches?.(
                        ".sss-lb-roadmap-detail, [class*='roadmap-detail']"
                    )
            )
        ];


        const candidates = [];


        orderedNodes.forEach(node => {
            const title =
                cleanRewardProductTitle(
                    textOf(node)
                );

            if (title) {
                candidates.push(title);
            }
        });


        if (!candidates.length) {
            return "";
        }


        candidates.sort(
            (a, b) =>
                a.length -
                b.length
        );


        return candidates[0];
    }


    function discoverLoyaltyRewards(block) {
        if (!block) {
            return {
                rewards: [],
                supported: false,
                expectedCount: 0
            };
        }


        const tiles =
            Array.from(
                block.querySelectorAll(
                    ".sss-lb-roadmap-tile[data-lb-reward-idx], [class*='roadmap-tile'][data-lb-reward-idx]"
                )
            );


        const freeProductTiles =
            tiles.filter(tile => {
                const label =
                    textOf(
                        tile.querySelector(
                            ".sss-lb-roadmap-title-label, [class*='roadmap-title-label']"
                        )
                    );

                return (
                    /^free\s+product$/i.test(
                        label
                    )
                );
            });


        const explicitlyTypedTiles =
            tiles.filter(tile => {
                const label =
                    textOf(
                        tile.querySelector(
                            ".sss-lb-roadmap-title-label, [class*='roadmap-title-label']"
                        )
                    );

                return !!label;
            });


        const hasUnsupportedRewardType =
            explicitlyTypedTiles.some(tile => {
                const label =
                    textOf(
                        tile.querySelector(
                            ".sss-lb-roadmap-title-label, [class*='roadmap-title-label']"
                        )
                    );

                return (
                    label &&
                    !/^free\s+product$/i.test(
                        label
                    )
                );
            });


        if (hasUnsupportedRewardType) {
            return {
                rewards: [],
                supported: false,
                expectedCount:
                    freeProductTiles.length
            };
        }


        let rewardIndexes = [
            ...new Set(
                freeProductTiles
                    .map(
                        tile =>
                            clean(
                                tile.getAttribute(
                                    "data-lb-reward-idx"
                                )
                            )
                    )
                    .filter(
                        value =>
                            value !== ""
                    )
            )
        ];


        if (!rewardIndexes.length) {
            rewardIndexes = [
                ...new Set(
                    Array.from(
                        block.querySelectorAll(
                            "[data-lb-reward-idx]"
                        )
                    )
                        .map(
                            element =>
                                clean(
                                    element.getAttribute(
                                        "data-lb-reward-idx"
                                    )
                                )
                        )
                        .filter(Boolean)
                )
            ];
        }


        const rewards = [];


        rewardIndexes.forEach(
            rewardIndex => {
                const nodes =
                    getRewardNodes(
                        block,
                        rewardIndex
                    );

                const order =
                    extractRewardOrder(
                        nodes
                    );

                const productTitle =
                    extractRewardProductTitle(
                        nodes
                    );


                if (
                    !Number.isFinite(order) ||
                    !productTitle
                ) {
                    return;
                }


                rewards.push({
                    rewardIndex:
                        Number.isFinite(
                            Number(rewardIndex)
                        )
                            ? Number(rewardIndex)
                            : rewardIndex,

                    order,

                    productTitle,

                    displayName:
                        displayNameFromTitle(
                            productTitle
                        )
                });
            }
        );


        rewards.sort(
            (a, b) => {
                if (
                    a.order !==
                    b.order
                ) {
                    return (
                        a.order -
                        b.order
                    );
                }

                return (
                    Number(
                        a.rewardIndex
                    ) -
                    Number(
                        b.rewardIndex
                    )
                );
            }
        );


        return {
            rewards,
            supported: true,
            expectedCount:
                rewardIndexes.length
        };
    }


    function makeLoyaltySignature(
        block,
        rewards
    ) {
        return [
            getLoyaltyPlanId(block),

            ...rewards.map(
                reward => [
                    reward.rewardIndex,
                    reward.order,
                    normalise(
                        reward.productTitle
                    )
                ].join(":")
            )
        ].join("|");
    }


    function hideIntegratedLoyaltyBlock(block) {
        if (!block) {
            return;
        }

        if (
            !block.classList.contains(
                LOYALTY_HIDDEN_CLASS
            )
        ) {
            block.classList.add(
                LOYALTY_HIDDEN_CLASS
            );
        }

        block.setAttribute(
            "aria-hidden",
            "true"
        );
    }


    function restoreNativeLoyalty(root) {
        if (!root) {
            return;
        }

        root.querySelectorAll(
            `.${LOYALTY_HIDDEN_CLASS}`
        ).forEach(block => {
            block.classList.remove(
                LOYALTY_HIDDEN_CLASS
            );

            block.removeAttribute(
                "aria-hidden"
            );
        });
    }


    /* ============================================================
       TOTAL VALUE SYNCHRONISATION
       ============================================================ */

    function syncTotalBox(
        section,
        firstBoxGifts,
        loyaltyRewards,
        planPrice,
        card = findPlanCard(section)
    ) {
        if (
            !section ||
            !section.isConnected
        ) {
            return;
        }


        const total =
            getCombinedOfferValue(
                firstBoxGifts,
                loyaltyRewards,
                card
            );


        if (
            !total ||
            !planPrice
        ) {
            return;
        }


        const formattedTotal =
            formatAmountLikeSite(
                total.amount,
                planPrice,
                total.decimalPlaces
            );


        let box =
            section.querySelector(
                ".ssl-total-box"
            );


        if (!box) {
            box =
                buildTotalBox(
                    firstBoxGifts,
                    planPrice,
                    loyaltyRewards,
                    section.dataset
                        .sslPlanDisplayPrice ||
                    planPrice,
                    section.dataset
                        .sslPlanCycle ||
                    "/mo",
                    card
                );

            if (box) {
                section.appendChild(box);
            }

            return;
        }


        const amount =
            box.querySelector(
                ".ssl-total-amount"
            );


        if (
            amount &&
            amount.textContent !==
            formattedTotal
        ) {
            amount.textContent =
                formattedTotal;
        }


        const subtitle = box.querySelector(".ssl-total-sub");
        if (subtitle && subtitle.textContent !== total.subtitle) {
            subtitle.textContent = total.subtitle;
        }


        const planValue =
            box.querySelector(
                ".ssl-total-plan-value"
            );

        const displayPlanPrice =
            section.dataset
                .sslPlanDisplayPrice ||
            planPrice;


        if (
            planValue &&
            planValue.textContent !==
            displayPlanPrice
        ) {
            planValue.textContent =
                displayPlanPrice;
        }
    }


    function clearIntegratedLoyalty(
        section,
        firstBoxGifts = [],
        planPrice = ""
    ) {
        if (!section) {
            return;
        }

        loyaltyValueCache.delete(section);


        section.querySelectorAll(
            ".ssl-item--loyalty"
        ).forEach(
            item =>
                item.remove()
        );


        const loyaltySection =
            section.querySelector(
                ".ssl-loyalty-section"
            );


        if (loyaltySection) {
            loyaltySection.hidden =
                true;
        }


        delete section.dataset
            .sslLoyaltySignature;


        updateGridColumns(section);


        if (
            firstBoxGifts.length &&
            planPrice
        ) {
            syncTotalBox(
                section,
                firstBoxGifts,
                [],
                planPrice
            );
        }
    }


    /* ============================================================
       FIRST-BOX GIFT CARD
       ============================================================ */

    function buildGift(gift) {
        const item =
            makeElement(
                "div",
                "ssl-item ssl-item--first-box"
            );

        const imageBox =
            makeElement(
                "div",
                "ssl-image-box"
            );

        const image =
            document.createElement(
                "img"
            );


        image.className =
            "ssl-image";

        image.src =
            gift.imageUrl;

        image.alt =
            gift.displayName;

        image.loading =
            "lazy";

        image.decoding =
            "async";


        imageBox.appendChild(image);


        const name =
            makeElement(
                "div",
                "ssl-name",
                gift.displayName
            );

        const price =
            makeElement(
                "div",
                "ssl-price",
                gift.value
            );

        const free =
            makeElement(
                "span",
                "ssl-free",
                "FREE"
            );


        item.append(
            imageBox,
            name,
            price,
            free
        );


        return item;
    }


    /* ============================================================
       LOYALTY REWARD CARD
       ============================================================ */

    function buildLoyaltyRewardCard(reward) {
        const item =
            makeElement(
                "div",
                "ssl-item ssl-item--loyalty"
            );


        item.dataset
            .sslLoyaltyRewardIndex =
            String(
                reward.rewardIndex
            );


        const imageBox =
            makeElement(
                "div",
                "ssl-image-box"
            );

        const image =
            document.createElement(
                "img"
            );


        image.className =
            "ssl-image";

        image.src =
            reward.imageUrl;

        image.alt =
            reward.displayName;

        image.loading =
            "lazy";

        image.decoding =
            "async";


        imageBox.appendChild(image);


        const name =
            makeElement(
                "div",
                "ssl-name",
                `GIFT: ${reward.displayName}`
            );

        const price =
            makeElement(
                "div",
                "ssl-price",
                formatMoney(
                    reward.originalValue
                )
            );

        const free =
            makeElement(
                "span",
                "ssl-free",
                "FREE"
            );

        const order =
            makeElement(
                "div",
                "ssl-loyalty-order",
                `IN ORDER ${reward.order}`
            );


        item.append(
            imageBox,
            name,
            price,
            free,
            order
        );


        return item;
    }


    /* ============================================================
       FREE SHIPPING ROW
       ============================================================ */

    function buildShippingRow() {
        const row =
            makeElement(
                "div",
                "ssl-shipping-row"
            );

        const left =
            makeElement(
                "div",
                "ssl-shipping-left"
            );

        const icon =
            makeElement(
                "span",
                "ssl-shipping-icon"
            );


        icon.setAttribute(
            "aria-hidden",
            "true"
        );


        icon.innerHTML = `
            <svg
                viewBox="0 0 24 24"
                aria-hidden="true"
                focusable="false"
            >
                <path d="M3 6.5h11v9H3z"></path>
                <path d="M14 9.5h3.2l3 3v3H14z"></path>
                <circle cx="7" cy="17.5" r="1.8"></circle>
                <circle cx="17.5" cy="17.5" r="1.8"></circle>
                <path d="M3 15.5h2.2"></path>
                <path d="M8.8 15.5h6.9"></path>
            </svg>
        `;


        const label =
            makeElement(
                "span",
                "ssl-shipping-label",
                "Free delivery forever"
            );

        const free =
            makeElement(
                "span",
                "ssl-shipping-free",
                "FREE"
            );


        left.append(
            icon,
            label
        );

        row.append(
            left,
            free
        );


        return row;
    }


    /* ============================================================
       TOTAL VALUE BOX
       ============================================================ */

    function buildTotalBox(
        firstBoxGifts,
        planPrice,
        loyaltyRewards = [],
        planDisplayPrice = planPrice,
        planCycle = "/mo",
        card = null
    ) {
        const total =
            getCombinedOfferValue(
                firstBoxGifts,
                loyaltyRewards,
                card
            );


        if (
            !total ||
            !planPrice
        ) {
            return null;
        }


        const formattedTotal =
            formatAmountLikeSite(
                total.amount,
                planPrice,
                total.decimalPlaces
            );


        const box =
            makeElement(
                "div",
                "ssl-total-box"
            );

        const copy =
            makeElement(
                "div",
                "ssl-total-copy"
            );

        const main =
            makeElement(
                "div",
                "ssl-total-main"
            );

        const label =
            makeElement(
                "span",
                "ssl-total-label",
                "TOTAL VALUE"
            );

        const amount =
            makeElement(
                "span",
                "ssl-total-amount",
                formattedTotal
            );

        const sub =
            makeElement(
                "div",
                "ssl-total-sub",
                total.subtitle
            );


        main.append(
            label,
            amount
        );

        copy.append(
            main,
            sub
        );


        const priceArea =
            makeElement(
                "div",
                "ssl-total-price"
            );

        const yours =
            makeElement(
                "div",
                "ssl-total-yours",
                "ONLY"
            );

        const priceLine =
            makeElement(
                "div",
                "ssl-total-plan-line"
            );

        const planValue =
            makeElement(
                "span",
                "ssl-total-plan-value",
                planDisplayPrice
            );


        priceLine.appendChild(
            planValue
        );


        if (planCycle) {
            const cycle =
                makeElement(
                    "span",
                    "ssl-total-cycle",
                    planCycle
                );

            priceLine.appendChild(
                cycle
            );
        }


        priceArea.append(
            yours,
            priceLine
        );

        box.append(
            copy,
            priceArea
        );


        return box;
    }


    /* ============================================================
       FIRST-BOX SECTION
       ============================================================ */

    function makeFirstBoxSignature(
        gifts,
        planPrice,
        freeShipping,
        planDisplayPrice,
        planCycle
    ) {
        return [
            planPrice,

            planDisplayPrice || "",

            planCycle || "",

            freeShipping
                ? "free-shipping-on"
                : "free-shipping-off",

            ...gifts.map(
                gift => [
                    gift.variantId,
                    gift.quantity,
                    gift.recurrence,
                    gift.imageUrl,
                    gift.value,
                    gift.productTitle
                ].join("::")
            )
        ].join("||");
    }


    function buildSection(
        gifts,
        planKey,
        signature,
        planPrice,
        freeShipping,
        planDisplayPrice,
        planCycle,
        card
    ) {
        const section =
            makeElement(
                "div",
                CUSTOM_CLASS
            );


        section.dataset.ssfgPlanKey =
            planKey;

        section.dataset.ssfgSignature =
            signature;

        section.dataset.ssfgBaseGiftCount =
            String(
                gifts.length
            );

        section.dataset.sslPlanDisplayPrice =
            planDisplayPrice ||
            planPrice ||
            "";

        section.dataset.sslPlanCycle =
            planCycle || "";


        section.setAttribute(
            "aria-label",
            "Free gifts with first subscription box"
        );


        const heading =
            makeElement(
                "div",
                "ssl-heading ssl-heading--first-box",
                "FREE WITH YOUR FIRST BOX"
            );

        const grid =
            makeElement(
                "div",
                "ssl-grid ssl-grid--first-box"
            );


        gifts.forEach(gift => {
            grid.appendChild(
                buildGift(gift)
            );
        });


        const loyaltySection =
            makeElement(
                "div",
                "ssl-loyalty-section"
            );

        loyaltySection.hidden = true;


        const loyaltyHeading =
            makeElement(
                "div",
                "ssl-heading ssl-heading--loyalty",
                "FUTURE LOYALTY GIFTS"
            );

        const loyaltyGrid =
            makeElement(
                "div",
                "ssl-grid ssl-grid--loyalty"
            );


        loyaltySection.append(
            loyaltyHeading,
            loyaltyGrid
        );


        section.append(
            heading,
            grid,
            loyaltySection
        );


        if (freeShipping) {
            section.appendChild(
                buildShippingRow()
            );
        }


        const totalBox =
            buildTotalBox(
                gifts,
                planPrice,
                [],
                planDisplayPrice,
                planCycle,
                card
            );


        if (totalBox) {
            section.appendChild(
                totalBox
            );
        }


        updateGridColumns(section);


        return section;
    }


    function findExistingSection(
        block,
        planKey
    ) {
        const parent =
            block.parentElement;

        if (!parent) {
            return null;
        }


        return (
            Array.from(
                parent.querySelectorAll(
                    `:scope > .${CUSTOM_CLASS}`
                )
            ).find(
                section =>
                    section.dataset
                        .ssfgPlanKey ===
                    planKey
            ) ||
            null
        );
    }


    function hideNativeGiftBlock(block) {
        block.classList.add(
            NATIVE_HIDDEN_CLASS
        );

        block.setAttribute(
            "aria-hidden",
            "true"
        );
    }


    function showNativeGiftBlock(block) {
        block.classList.remove(
            NATIVE_HIDDEN_CLASS
        );

        block.removeAttribute(
            "aria-hidden"
        );
    }


    /* ============================================================
       ASYNC LOYALTY INTEGRATION
       ============================================================ */

    async function syncLoyaltyIntoSection(
        root,
        card,
        section,
        firstBoxGifts,
        planPrice
    ) {
        if (
            !root ||
            !card ||
            !section ||
            !section.isConnected
        ) {
            return;
        }


        const loyaltyBlock =
            findLoyaltyBlockForCard(
                root,
                card
            );


        if (!loyaltyBlock) {
            clearIntegratedLoyalty(
                section,
                firstBoxGifts,
                planPrice
            );

            restoreNativeLoyalty(root);

            return;
        }


        const discovery =
            discoverLoyaltyRewards(
                loyaltyBlock
            );


        if (
            !discovery.supported ||
            !discovery.expectedCount ||
            discovery.rewards.length !==
            discovery.expectedCount
        ) {
            clearIntegratedLoyalty(
                section,
                firstBoxGifts,
                planPrice
            );

            restoreNativeLoyalty(root);

            return;
        }


        const signature =
            makeLoyaltySignature(
                loyaltyBlock,
                discovery.rewards
            );


        if (
            section.dataset
                .sslLoyaltySignature ===
            signature
        ) {
            const loyaltySection =
                section.querySelector(
                    ".ssl-loyalty-section"
                );

            if (
                loyaltySection &&
                loyaltySection.querySelector(
                    ".ssl-item--loyalty"
                )
            ) {
                loyaltySection.hidden =
                    false;
            }

            hideIntegratedLoyaltyBlock(
                loyaltyBlock
            );

            return;
        }


        if (
            loyaltyPending.get(
                section
            ) === signature
        ) {
            return;
        }


        loyaltyValueCache.delete(section);

        loyaltyPending.set(
            section,
            signature
        );


        let resolvedRewards;


        try {
            resolvedRewards =
                await Promise.all(
                    discovery.rewards.map(
                        resolveLoyaltyReward
                    )
                );
        } catch (error) {
            console.warn(
                "[Super Subscriptions Loyalty Integration] Reward discovered, but Shopify product resolution failed. Native Loyalty display retained.",
                error,
                discovery.rewards
            );


            if (
                loyaltyPending.get(
                    section
                ) === signature
            ) {
                loyaltyPending.delete(
                    section
                );
            }


            clearIntegratedLoyalty(
                section,
                firstBoxGifts,
                planPrice
            );

            restoreNativeLoyalty(root);

            return;
        }


        if (
            loyaltyPending.get(
                section
            ) === signature
        ) {
            loyaltyPending.delete(
                section
            );
        }


        if (
            !section.isConnected ||
            !root.isConnected
        ) {
            return;
        }


        const latestBlock =
            findLoyaltyBlockForCard(
                root,
                card
            );

        if (!latestBlock) {
            return;
        }


        const latestDiscovery =
            discoverLoyaltyRewards(
                latestBlock
            );

        const latestSignature =
            makeLoyaltySignature(
                latestBlock,
                latestDiscovery.rewards
            );


        if (
            latestSignature !==
            signature
        ) {
            scheduleRefresh();
            return;
        }


        const grid =
            section.querySelector(
                ".ssl-grid--loyalty"
            );


        if (!grid) {
            restoreNativeLoyalty(root);
            return;
        }


        grid.querySelectorAll(
            ".ssl-item--loyalty"
        ).forEach(
            item =>
                item.remove()
        );


        resolvedRewards.forEach(
            reward => {
                grid.appendChild(
                    buildLoyaltyRewardCard(
                        reward
                    )
                );
            }
        );


        const loyaltySection =
            section.querySelector(
                ".ssl-loyalty-section"
            );


        if (
            loyaltySection &&
            resolvedRewards.length
        ) {
            loyaltySection.hidden =
                false;
        }


        loyaltyValueCache.set(section, resolvedRewards);

        syncTotalBox(
            section,
            firstBoxGifts,
            resolvedRewards,
            planPrice,
            card
        );


        section.dataset.sslLoyaltySignature =
            signature;


        updateGridColumns(section);


        hideIntegratedLoyaltyBlock(
            latestBlock
        );
    }


    /* ============================================================
       ENHANCE ONE FIRST-BOX GIFT BLOCK
       ============================================================ */

    function enhanceGiftBlock(
        root,
        block
    ) {
        if (
            !block ||
            !block.isConnected
        ) {
            return;
        }


        const ready =
            block.getAttribute(
                "data-gift-ready"
            );


        if (
            ready !== null &&
            ready !== "1"
        ) {
            return;
        }


        const planKey =
            getPlanKey(block);


        if (!planKey) {
            return;
        }


        const rows =
            getEligibleRows(block);

        const existing =
            findExistingSection(
                block,
                planKey
            );


        if (!rows.length) {
            existing?.remove();

            showNativeGiftBlock(
                block
            );

            restoreNativeLoyalty(root);

            return;
        }


        const gifts =
            rows
                .map(
                    row =>
                        extractGift(
                            row,
                            block
                        )
                )
                .filter(Boolean);


        if (
            !gifts.length ||
            gifts.length !==
            rows.length
        ) {
            existing?.remove();

            showNativeGiftBlock(
                block
            );

            restoreNativeLoyalty(root);

            return;
        }


        const card =
            findPlanCard(block);

        const planPrice =
            getDisplayedPlanPrice(
                card
            );

        const freeShipping =
            isFreeShippingEnabled(
                card
            );


        /*
          Important:

          planPrice may be Super's full prepaid checkout total.

          pricePresentation converts prepaid plans to an effective
          per-month/per-delivery display value for the black
          "yours for" Total Value bar.
        */

        const pricePresentation =
            getTotalPricePresentation(
                card,
                planPrice
            );

        const planDisplayPrice =
            pricePresentation.value ||
            planPrice;

        const planCycle =
            pricePresentation.cycle ||
            "";


        const signature =
            makeFirstBoxSignature(
                gifts,
                planPrice,
                freeShipping,
                planDisplayPrice,
                planCycle
            );


        let section =
            existing;


        if (
            !section ||
            section.dataset
                ?.ssfgSignature !==
            signature
        ) {
            section =
                buildSection(
                    gifts,
                    planKey,
                    signature,
                    planPrice,
                    freeShipping,
                    planDisplayPrice,
                    planCycle,
                    card
                );


            if (existing) {
                existing.replaceWith(
                    section
                );
            } else {
                block.insertAdjacentElement(
                    "afterend",
                    section
                );
            }
        }


        hideNativeGiftBlock(block);


        if (card) {
            // A cached loyalty grid may already exist when the policy
            // fetch finishes. Recompute the value without rebuilding it.
            syncTotalBox(
                section,
                gifts,
                loyaltyValueCache.get(section) || [],
                planPrice,
                card
            );

            syncLoyaltyIntoSection(
                root,
                card,
                section,
                gifts,
                planPrice
            );
        }
    }


    /* ============================================================
       CLEAN STALE SECTIONS
       ============================================================ */

    function cleanupStaleSections(root) {
        const liveKeys =
            new Set(
                Array.from(
                    root.querySelectorAll(
                        GIFT_BLOCK_SELECTOR
                    )
                )
                    .map(getPlanKey)
                    .filter(Boolean)
            );


        root.querySelectorAll(
            `.${CUSTOM_CLASS}`
        ).forEach(section => {
            const key =
                section.dataset
                    .ssfgPlanKey ||
                "";

            if (
                key &&
                !liveKeys.has(key)
            ) {
                section.remove();
            }
        });
    }


    /* ============================================================
       THEME FONT — STYLE ONLY
       Keep widget text synced with the active Shopify theme's
       computed body font, without changing Super's price data.
       ============================================================ */
    function syncThemeFont(root) {
        const body = document.body || document.documentElement;
        if (!root || !body) return;

        const themeFont = window.getComputedStyle(body).fontFamily;
        if (!themeFont) return;

        // Avoid redundant inline-style mutations / refresh loops.
        if (root.style.getPropertyValue("--ssfg-theme-font-family") !== themeFont) {
            root.style.setProperty("--ssfg-theme-font-family", themeFont);
        }
    }


    /* ============================================================
       ENHANCE ALL
       ============================================================ */

    function enhanceAll() {
        if (enhancing) {
            return;
        }

        enhancing = true;


        try {
            document.querySelectorAll(
                ROOT_SELECTOR
            ).forEach(root => {
                syncThemeFont(root);

                root.querySelectorAll(
                    PLAN_SELECTOR
                ).forEach(card => {
                    enhanceDescriptionPill(
                        card
                    );

                    enhanceSaveBadge(
                        card
                    );
                });


                root.querySelectorAll(
                    GIFT_BLOCK_SELECTOR
                ).forEach(block => {
                    enhanceGiftBlock(
                        root,
                        block
                    );
                });


                root.querySelectorAll(PLAN_SELECTOR).forEach(card => {
                    enhancePrepaidTopPrice(card);
                });

                cleanupStaleSections(
                    root
                );
            });
        } catch (error) {
            console.warn(
                "[Super Subscriptions First Box + Loyalty]",
                error
            );
        } finally {
            enhancing = false;
        }
    }


    /* ============================================================
       FAST REFRESH SCHEDULER
       ============================================================ */

    function scheduleRefresh() {
        if (
            refreshFrame !== null
        ) {
            return;
        }


        refreshFrame =
            window.requestAnimationFrame(
                () => {
                    refreshFrame = null;

                    enhanceAll();
                }
            );
    }


    /* ============================================================
       INITIAL LOAD
       ============================================================ */

    function initialise() {
        enhanceAll();


        [
            150,
            500,
            1500
        ].forEach(delay => {
            window.setTimeout(
                enhanceAll,
                delay
            );
        });
    }


    if (
        document.readyState ===
        "loading"
    ) {
        document.addEventListener(
            "DOMContentLoaded",
            initialise,
            {
                once: true
            }
        );
    } else {
        initialise();
    }


    window.addEventListener(
        "load",
        () => {
            window.requestAnimationFrame(
                enhanceAll
            );
        },
        {
            once: true
        }
    );


    /* ============================================================
       SUPER INTERACTIONS
       ============================================================ */

    document.addEventListener(
        "change",
        event => {
            if (
                event.target
                    .closest?.(
                        ROOT_SELECTOR
                    )
            ) {
                scheduleRefresh();

                window.setTimeout(
                    enhanceAll,
                    180
                );

                window.setTimeout(
                    enhanceAll,
                    450
                );
            }
        }
    );


    document.addEventListener(
        "click",
        event => {
            if (
                event.target
                    .closest?.(
                        ROOT_SELECTOR
                    )
            ) {
                scheduleRefresh();
            }
        }
    );


    /* ============================================================
       WATCH SUPER RERENDERS
       ============================================================ */

    const observer =
        new MutationObserver(
            mutations => {
                const relevant =
                    mutations.some(
                        mutation => {
                            const target =
                                mutation.target
                                    ?.nodeType === 1
                                    ? mutation.target
                                    : mutation.target
                                        ?.parentElement;


                            if (
                                target
                                    ?.closest?.(
                                        ROOT_SELECTOR
                                    )
                            ) {
                                return true;
                            }


                            return Array.from(
                                mutation.addedNodes
                            ).some(node => {
                                if (
                                    node.nodeType !== 1
                                ) {
                                    return false;
                                }


                                return (
                                    node.matches?.(
                                        ROOT_SELECTOR
                                    ) ||
                                    !!node.querySelector?.(
                                        ROOT_SELECTOR
                                    ) ||
                                    node.matches?.(
                                        PLAN_SELECTOR
                                    ) ||
                                    !!node.querySelector?.(
                                        PLAN_SELECTOR
                                    ) ||
                                    node.matches?.(
                                        GIFT_BLOCK_SELECTOR
                                    ) ||
                                    !!node.querySelector?.(
                                        GIFT_BLOCK_SELECTOR
                                    ) ||
                                    node.matches?.(
                                        LOYALTY_SELECTOR
                                    ) ||
                                    !!node.querySelector?.(
                                        LOYALTY_SELECTOR
                                    ) ||
                                    node.matches?.(
                                        DESCRIPTION_SELECTOR
                                    ) ||
                                    !!node.querySelector?.(
                                        DESCRIPTION_SELECTOR
                                    )
                                );
                            });
                        }
                    );


                if (relevant) {
                    scheduleRefresh();
                }
            }
        );


    observer.observe(
        document.documentElement,
        {
            childList: true,
            subtree: true,
            characterData: true,
            attributes: true,

            attributeFilter: [
                "class",
                "style",
                "hidden",
                "src",
                "srcset",
                "sss-selling-plan-id",
                "selling-plan-id",
                "data-selling-plan-id",
                "data-lb-reward-idx",
                "data-gift-ready",
                "data-gift-variant-id",
                "data-gift-quantity",
                "data-gift-recurrence"
            ]
        }
    );

})();